import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
  ViewChild,
  AfterViewInit,
  effect,
} from '@angular/core';
import { SetupNodeComponent } from '../../setup-node/setup-node.component';
import { DxTextBoxComponent, DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxValidatorComponent, DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { DxFormComponent, DxiFormItemComponent } from 'devextreme-angular/ui/form';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { DxPopoverComponent } from 'devextreme-angular/ui/popover';
import { Phase } from '../../../../../../../interfaces/entities/project-configuration';

@Component({
    selector: 'app-setup-phase-node',
    imports: [
        CommonModule,
        SetupNodeComponent,
        DxValidatorModule,
        DxiValidationRuleComponent,
        SvgIconComponent,
        DxFormComponent,
        DxiFormItemComponent,
        DxPopoverComponent,
        DxTextBoxModule,
    ],
    templateUrl: './setup-phase-node.component.html',
    styleUrl: './setup-phase-node.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupPhaseNodeComponent implements AfterViewInit {
  readonly messageShowInGantt = $localize`:@@showInGantt:Show in Gantt`;

  // Bind the validation callback in constructor to ensure proper context
  public uniqueNameValidationCallback: (params: any) => boolean;

  phase = model.required<Phase>();
  phases = model.required<Phase[]>();
  editing = model<boolean>();
  expanded = model<boolean>(true);
  validationGroup = input<string>();
  allowDelete = input<boolean>();
  onDelete = output<Phase>();
  onPhaseChange = output<Phase>();

  private changes: boolean = false;
  public isValid: boolean = null;
  private callbacks: Array<() => void> = [];

  adapterConfig = {
    getValue: () => {
      return this.validationCallback();
    },
    // validationRequestsCallbacks: this.callbacks,
  };

  @ViewChild(DxFormComponent)
  form!: DxFormComponent;

  constructor() {
    // Bind the validation callback to ensure proper context
    this.uniqueNameValidationCallback = this.validateUniqueName.bind(this);
    this.phases.subscribe(() => {
      setTimeout(() => {
        if (this.form?.instance) {
          this.form.instance.validate();
        }
        this.changes = true; // Force validation callback to re-run
      }, 0);
    });
  }

  // Validation callback for the custom validation rule
  validationCallback = () => {
    // Always revalidate if there were changes OR if isValid is null (first time or forced)
    if (this.changes || this.isValid === null) {
      this.isValid = this.validateForm();
      this.changes = false;
    }
    const isValid = this.isValid ?? this.validateForm();
    return isValid;
  };

  ngAfterViewInit() {}

  private validateForm(): boolean {
    if (this.form?.instance) {
      const formValidation = this.form.instance.validate();
      this.isValid = formValidation?.isValid ?? false;
      this.revalidate();
      return this.isValid;
    }
    return false;
  }

  private revalidate() {
    this.callbacks.forEach(func => {
      func();
    });
  }
  toggleExpanded() {
    this.expanded.update(x => !x);
  }

  onDataChanged(e: FieldDataChangedEvent) {
    this.changes = true;
  }

  onDeleteClicked() {
    this.onDelete.emit(this.phase());
  }

  /**
   * Method version of the unique name validation for proper binding
   */
  private validateUniqueName(params: any): boolean {
    console.log('Validate Unique Name');

    const currentName = params?.value || params?.data || params;

    // If no name entered, let the required validation handle it
    if (!currentName || currentName.trim() === '') {
      return true;
    }

    // Get the current phase to exclude it from duplicate check
    const currentPhase = this.phase();
    const allPhases = this.phases();

    // Check if any other phase has the same name (case-insensitive)
    const duplicateExists = allPhases.some(phase => {
      // Skip the current phase we're editing (using id comparison)
      if (phase.id === currentPhase.id) {
        return false;
      }

      const hasName = phase.title;
      const isSameName = hasName && phase.title.trim().toLowerCase() === currentName.trim().toLowerCase();

      return hasName && isSameName;
    });

    return !duplicateExists;
  }
}
